const STORAGE_KEY = "slowedMemory";
const MAX_RECENTS = 6;
// Mood plays with the time they happened, newest first, so the Moods row can
// learn what someone plays and at which hour. Capped so old taste fades out.
const MAX_HISTORY = 60;
const LOCALSTORAGE_KEY = "slowed:slowedMemory";

const DEFAULT_MEMORY = {
  recents: [],
  likes: [],
  customMoods: [],
  history: [],
};

function isHistoryEntry(entry) {
  return (
    !!entry &&
    typeof entry.name === "string" &&
    !!entry.name &&
    (typeof entry.at === "number" || entry.at === null)
  );
}

function hasChromeStorage() {
  try {
    return !!(self.chrome && self.chrome.storage && self.chrome.storage.local);
  } catch (_e) {
    return false;
  }
}

async function readRaw() {
  if (hasChromeStorage()) {
    return new Promise((resolve) => {
      self.chrome.storage.local.get([STORAGE_KEY], (data) => {
        resolve((data && data[STORAGE_KEY]) || null);
      });
    });
  }
  try {
    const raw = window.localStorage.getItem(LOCALSTORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
}

async function writeRaw(memory) {
  if (hasChromeStorage()) {
    return new Promise((resolve) => {
      self.chrome.storage.local.set({ [STORAGE_KEY]: memory }, () => resolve());
    });
  }
  try {
    window.localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(memory));
  } catch (_e) {
    /* ignore quota / sandbox errors */
  }
}

function normalize(memory) {
  if (!memory || typeof memory !== "object") return { ...DEFAULT_MEMORY };
  return {
    recents: Array.isArray(memory.recents) ? memory.recents.slice(0, MAX_RECENTS) : [],
    likes: Array.isArray(memory.likes) ? memory.likes : [],
    customMoods: Array.isArray(memory.customMoods) ? memory.customMoods : [],
    history: Array.isArray(memory.history)
      ? memory.history.filter(isHistoryEntry).slice(0, MAX_HISTORY)
      : [],
  };
}

export async function getSlowedMemory() {
  return normalize(await readRaw());
}

/**
 * Plays to rank moods by, newest first. Recents written before the history
 * existed have no time; they still count as plays, just not for the hour.
 */
export function getMoodPlays(memory) {
  const { history = [], recents = [] } = memory || {};
  if (history.length) return history;
  return recents.map((name) => ({ name, at: null }));
}

function toHistoryEntry({ name, at }) {
  return { name, at: typeof at === "number" ? at : null };
}

export async function recordMoodUsed(name, at = Date.now()) {
  if (!name) return null;
  const memory = await getSlowedMemory();
  const next = [name, ...memory.recents.filter((n) => n !== name)].slice(
    0,
    MAX_RECENTS,
  );
  // The first timed play carries the untimed recents along, so switching to
  // the history does not forget what was played before it existed.
  const history = [{ name, at }, ...getMoodPlays(memory).map(toHistoryEntry)]
    .slice(0, MAX_HISTORY);
  const updated = { ...memory, recents: next, history };
  await writeRaw(updated);
  return updated;
}

export async function toggleMoodLike(name) {
  if (!name) return null;
  const memory = await getSlowedMemory();
  const isLiked = memory.likes.includes(name);
  const likes = isLiked
    ? memory.likes.filter((n) => n !== name)
    : [...memory.likes, name];
  const updated = { ...memory, likes };
  await writeRaw(updated);
  return updated;
}

